/**
 * PATH: packages/ui/src/shortcuts/search.ts
 * FILE_ID: UI-SHORTCUTS-SEARCH-001
 * PURPOSE: Pure filter for the shortcuts reference search box.
 * WHY: Kept separate from the registry data + the React component so the
 *      matching logic is testable in isolation (happy path + no-match).
 *
 * FLOW:
 *   ┌──────────────────────┐   ┌────────────┐   ┌────────────────────────┐
 *   │ ShortcutHubSection[]  │ → │ query str  │ → │ ShortcutHubSection[]    │
 *   │ + query               │   │ normalised │   │ (only matching entries) │
 *   └──────────────────────┘   └────────────┘   └────────────────────────┘
 *
 * RELATED:
 *   - ./registry.ts: provides the section shape + data
 */

import type { ShortcutEntry, ShortcutHubSection } from './registry'

/**
 * matchesQuery — does a single shortcut match the (already-lowercased) query?
 * Matches against the description, the joined keys, and the category so an
 * operator can search "view", "ctrl", "⌘", "archive", etc.
 */
export function matchesQuery(entry: ShortcutEntry, loweredQuery: string): boolean {
  if (!loweredQuery) return true
  const haystack = [
    entry.description,
    entry.category ?? '',
    entry.keys.join(' '),
    entry.keys.join(''),
  ]
    .join(' ')
    .toLowerCase()
  return haystack.includes(loweredQuery)
}

/**
 * filterShortcutSections — return only the sections (and within them, only the
 * entries) that match the query. Sections with zero matches are dropped so the
 * UI never renders an empty hub. An empty/whitespace query returns the input
 * unchanged (all sections, all entries).
 *
 * Pure: no mutation of the input array or its entries.
 */
export function filterShortcutSections(
  sections: ShortcutHubSection[],
  query: string,
): ShortcutHubSection[] {
  const lowered = query.trim().toLowerCase()
  if (!lowered) return sections

  const result: ShortcutHubSection[] = []
  for (const section of sections) {
    const shortcuts = section.shortcuts.filter((s) => matchesQuery(s, lowered))
    if (shortcuts.length > 0) {
      result.push({ ...section, shortcuts })
    }
  }
  return result
}

/**
 * groupByCategory — turn a flat shortcut list into ordered [category, entries]
 * pairs, preserving first-seen category order. Entries with no category fall
 * under 'Other'. Pure.
 */
export function groupByCategory(
  shortcuts: ShortcutEntry[],
): Array<[string, ShortcutEntry[]]> {
  const order: string[] = []
  const buckets = new Map<string, ShortcutEntry[]>()
  for (const entry of shortcuts) {
    const cat = entry.category ?? 'Other'
    if (!buckets.has(cat)) {
      buckets.set(cat, [])
      order.push(cat)
    }
    buckets.get(cat)!.push(entry)
  }
  return order.map((cat) => [cat, buckets.get(cat)!] as [string, ShortcutEntry[]])
}
